import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { usePlaceholderContainer } from "@/components/Playground/Messages/PlaceholderContainer/context/placeholder-container-context";
import { useInputVariables } from "@/components/Playground/hooks/useInputVariables";

export const PlaceholderContainerHeaderVariableNameInput = observer(() => {
  const { message, readonly, leftBraces, rightBraces } = usePlaceholderContainer();
  const { inputVariables } = useInputVariables();

  // Local state for intermediary value
  const [localValue, setLocalValue] = useState(message.content);
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Sync local state when message.content changes externally
  useEffect(() => {
    setLocalValue(message.content);
    setIsDuplicate(false);
  }, [message.content]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Check if variable with this name already exists (excluding current)
    const currentName = message.name;
    const nameExists = inputVariables.some(v => v.key === newValue && v.key !== currentName);

    if (nameExists) {
      setIsDuplicate(true);
      // Don't update message.name
    } else {
      setIsDuplicate(false);
      message.setMessage(newValue);
    }
  };

  return (
    <BasicTooltip
      content="A variable with this name already exists"
      variant="red"
      contentClassName="!bg-red-50 !text-red-700 !border-red-200"
      shouldShow={isDuplicate}
      side="top"
    >
      <div
        className={cn(
          "flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1",
          "transition-border-color duration-300",
          { "!border-red-500": isDuplicate },
        )}
      >
        <span
          className={cn("text-xs text-gray-500", "transition-color duration-300", {
            "!text-red-500": isDuplicate,
          })}
        >
          {leftBraces}
        </span>

        <Input
          readOnly={readonly}
          placeholder="variable_name"
          onChange={handleOnChange}
          value={localValue}
          className={cn(
            "!h-6 !min-h-0 w-32 flex-1 !border-0 bg-transparent px-1 font-mono text-xs !outline-none !ring-0 focus:border-none focus:outline-none focus:ring-0",
            isDuplicate && "!text-red-500",
          )}
        />

        <span
          className={cn("text-xs text-gray-500", "transition-color duration-300", {
            "!text-red-500": isDuplicate,
          })}
        >
          {rightBraces}
        </span>
      </div>
    </BasicTooltip>
  );
});
